import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyAxios } from 'api/apiBase/axios';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { bootstrapStrategy } from '../bootstrap';
import { aggregateOrderbook } from '../utils';

const DIGIFINEX_REST_BASE = 'https://openapi.digifinex.com/v3';
const DIGIFINEX_PROXY_PATH = '/api/v1/proxy';
const DIGIFINEX_REST_POLL_INTERVAL_MS = 5_000;
const DIGIFINEX_MAX_BOOK_DEPTH = 150;
const DIGIFINEX_MAX_KLINES = 500;

const DIGIFINEX_PERIOD_BY_TIMEFRAME: Partial<Record<IChartTimeframe, string>> = {
  '1s': '1',
  '10s': '1',
  '30s': '1',
  '1m': '1',
  '3m': '5',
  '5m': '5',
  '15m': '15',
  '30m': '30',
  '1h': '60',
  '2h': '60',
  '4h': '240',
  '6h': '240',
  '8h': '240',
  '12h': '240',
  '24h': '1D',
  '1d': '1D',
  '2d': '1D',
  '3d': '1D',
  '5d': '1D',
  '168h': '1W',
  '1w': '1W',
  '1M': '1W',
};

interface DigiFinexEnvelope {
  code: number;
  msg?: string;
}

interface DigiFinexSymbol {
  symbol: string;
  status: string;
  price_precision: number;
}

interface DigiFinexTicker {
  symbol: string;
  sell: string | number;
  buy: string | number;
  last: string | number;
  high: string | number;
  low: string | number;
  change: string | number;
  vol: string | number;
  base_vol: string | number;
}

interface DigiFinexTrade {
  id: string | number;
  date: string | number;
  price: string | number;
  amount: string | number;
  type: string;
}

type DigiFinexRawKline = (string | number)[];

const assertSuccess = <T extends DigiFinexEnvelope>(response: T): T => {
  if (response.code !== 0) {
    throw new Error(`DigiFinex API ${response.code}: ${response.msg ?? 'request failed'}`);
  }
  return response;
};

const proxyGet = async <T extends DigiFinexEnvelope>(
  path: string,
  params: Record<string, string | number> = {},
): Promise<T> => {
  const target = new URL(`${DIGIFINEX_REST_BASE}${path}`);
  Object.entries(params).forEach(([key, value]) => target.searchParams.set(key, String(value)));
  const response = await proxyAxios.get<T>(DIGIFINEX_PROXY_PATH, {
    params: { url: target.toString() },
  });
  return assertSuccess(response.data);
};

const toMilliseconds = (value: string | number): number => {
  const timestamp = Number(value);
  return timestamp < 10_000_000_000 ? timestamp * 1_000 : timestamp;
};

const toSeconds = (value: number): number =>
  Math.floor(value < 10_000_000_000 ? value : value / 1_000);

export const normalizeDigiFinexKlines = (rows: DigiFinexRawKline[]): KLine[] =>
  rows
    .filter(
      row => row.length >= 6 && row.slice(0, 6).every(value => Number.isFinite(Number(value))),
    )
    .map(
      row =>
        [
          toMilliseconds(row[0]),
          String(row[5]),
          String(row[3]),
          String(row[4]),
          String(row[2]),
          String(row[1]),
        ] as KLine,
    )
    .sort((left, right) => left[0] - right[0]);

export const normalizeDigiFinexTrades = (rows: DigiFinexTrade[]): TradeSocket[] =>
  rows
    .filter(
      row =>
        Number(row.date) > 0 &&
        Number(row.price) > 0 &&
        Number(row.amount) > 0 &&
        (row.type === 'buy' || row.type === 'sell'),
    )
    .map(row => ({
      price: String(row.price),
      quantity: String(row.amount),
      timestamp: String(toMilliseconds(row.date)),
      side: row.type,
    }));

export class DigiFinexStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pollInterval: ReturnType<typeof setInterval> | undefined;
  pair: string;
  base: string;
  quote: string;
  depth: number;
  precision: number;
  selectedPrecision: number;
  activeIntervalMs: number;
  lastTradeSignature: string | undefined;
  asks: [string, string][];
  bids: [string, string][];

  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.base = base.toUpperCase();
    this.quote = quote.toUpperCase();
    this.pair = `${this.base}_${this.quote}`;
    this.depth = Math.min(orderbookDepth ?? 20, DIGIFINEX_MAX_BOOK_DEPTH);
    this.precision = 1;
    this.selectedPrecision = 1;
    this.activeIntervalMs = 60_000;
    this.asks = [];
    this.bids = [];
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  fetchTickerPrecision = async () => {
    try {
      const response = await proxyGet<DigiFinexEnvelope & { symbol_list: DigiFinexSymbol[] }>(
        '/trades/symbols',
      );
      const metadata = response.symbol_list?.find(
        item => item.symbol.toUpperCase() === this.pair && item.status === 'TRADING',
      );
      if (
        !metadata ||
        !Number.isInteger(metadata.price_precision) ||
        metadata.price_precision < 0
      ) {
        throw new Error(`DigiFinex symbol metadata is missing for ${this.pair}`);
      }
      this.precision = metadata.price_precision;
      this.selectedPrecision = metadata.price_precision;
      this.precisionUpdate(metadata.price_precision);
    } catch (error) {
      console.log('DigiFinex precision error:', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const response = await proxyGet<
        DigiFinexEnvelope & { asks: (string | number)[][]; bids: (string | number)[][] }
      >('/order_book', { symbol: this.pair, limit: this.depth });
      const normalize = (rows: (string | number)[][]): [string, string][] =>
        (rows ?? [])
          .filter(row => row.length >= 2 && Number(row[0]) > 0 && Number(row[1]) > 0)
          .map(row => [String(row[0]), String(row[1])]);
      this.asks = normalize(response.asks).sort(
        (left, right) => Number(left[0]) - Number(right[0]),
      );
      this.bids = normalize(response.bids).sort(
        (left, right) => Number(right[0]) - Number(left[0]),
      );
      this.publishOrderbook();
    } catch (error) {
      console.log('DigiFinex orderbook error:', error);
    }
  };

  fetchTicker = async () => {
    try {
      const response = await proxyGet<DigiFinexEnvelope & { ticker: DigiFinexTicker[] }>(
        '/ticker',
        { symbol: this.pair },
      );
      const ticker = response.ticker?.find(item => item.symbol.toUpperCase() === this.pair);
      if (!ticker) throw new Error(`DigiFinex ticker is missing for ${this.pair}`);
      this.onTickerUpdate({
        baseVolume: String(ticker.vol),
        change: normalizeNumber(Number(ticker.change)),
        close: String(ticker.last),
        high: String(ticker.high),
        last: String(ticker.last),
        low: String(ticker.low),
        quoteVolume: String(ticker.base_vol),
      });
    } catch (error) {
      console.log('DigiFinex ticker error:', error);
    }
  };

  fetchKlines = async ({
    from,
    to,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }): Promise<KLine[]> => {
    try {
      const response = await proxyGet<DigiFinexEnvelope & { data: DigiFinexRawKline[] }>('/kline', {
        symbol: this.pair,
        period: DIGIFINEX_PERIOD_BY_TIMEFRAME[interval] ?? '1',
        start_time: toSeconds(from),
        end_time: toSeconds(to),
      });
      return normalizeDigiFinexKlines(response.data ?? []).slice(
        -Math.min(Math.max(limit, 1), DIGIFINEX_MAX_KLINES),
      );
    } catch (error) {
      console.log('DigiFinex klines error:', error);
      return [];
    }
  };

  fetchTrades = async ({ limit }: { base: string; quote: string; limit: number }) => {
    try {
      const response = await proxyGet<DigiFinexEnvelope & { data: DigiFinexTrade[] }>('/trades', {
        symbol: this.pair,
        limit: Math.min(Math.max(limit, 1), 100),
      });
      return normalizeDigiFinexTrades(response.data ?? []);
    } catch (error) {
      console.log('DigiFinex trades error:', error);
      return [];
    }
  };

  publishOrderbook = () => {
    this.onOrderbookUpdate(
      aggregateOrderbook(this.asks, this.bids, this.selectedPrecision, this.depth),
    );
  };

  pollLive = async () => {
    const now = Date.now();
    const [, , trades, klines] = await Promise.all([
      this.fetchOrderbook(),
      this.fetchTicker(),
      this.fetchTrades({ base: this.base, quote: this.quote, limit: 1 }),
      this.fetchKlines({
        base: this.base,
        quote: this.quote,
        from: now - this.activeIntervalMs * 2,
        to: now + 1_000,
        limit: 2,
        interval: this.intervalToTimeframe(),
      }),
    ]);
    const trade = trades[0];
    if (trade) {
      const signature = `${trade.timestamp}:${trade.price}:${trade.quantity}:${trade.side}`;
      if (signature !== this.lastTradeSignature) {
        this.lastTradeSignature = signature;
        this.onTradeUpdate(trade);
      }
    }
    const kline = klines.at(-1);
    if (kline) this.klineUpdate(kline);
  };

  intervalToTimeframe = (): IChartTimeframe => {
    const mapping: Record<number, IChartTimeframe> = {
      60_000: '1m',
      300_000: '5m',
      900_000: '15m',
      1_800_000: '30m',
      3_600_000: '1h',
      14_400_000: '4h',
      86_400_000: '1d',
      604_800_000: '1w',
    };
    return mapping[this.activeIntervalMs] ?? '1m';
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    this.closeWebSocket();
    this.activeIntervalMs = interval;
    void this.pollLive();
    this.pollInterval = setInterval(() => void this.pollLive(), DIGIFINEX_REST_POLL_INTERVAL_MS);
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
      this.ws = undefined;
    }
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = undefined;
    }
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;
    this.publishOrderbook();
  };

  setOrderbookDepth = (depth: number) => {
    this.depth = Math.min(Math.max(depth, 1), DIGIFINEX_MAX_BOOK_DEPTH);
    void this.fetchOrderbook();
  };

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: Math.max(prec, 1) })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(value => ({ value: normalizeNumber(1 / 10 ** value), tick: value, id: value }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
    await Promise.all([this.fetchOrderbook(), this.fetchTicker()]);
  };

  static init = async (args: IInitArgs) => {
    const instance = new DigiFinexStrategy(args);
    await bootstrapStrategy(instance);
    return instance;
  };
}
